import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import { resolveSiteUrl } from './siteUrl';

/**
 * SEO Head Component
 * Manages all meta tags, Open Graph, and structured data
 */
const SEOHead = ({
  title = 'Home Maintenance in Kochi, Trivandrum & Calicut | Fixome',
  description = 'Book trusted technicians for plumbing, electrical, AC repair, painting, and more. On-site Monday to Sunday, 8am to 7pm.',
  keywords = 'home repair, plumbing service, electrician, AC repair, home maintenance, technician booking, emergency repair, handyman',
  image = '/logo_transparent.png',
  url = window.location.href,
  type = 'website',
  author = 'Fixome',
  publishedTime,
  modifiedTime,
  structuredData,
  canonical,
  noindex = false,
  nofollow = false,
}) => {
  const siteUrl = resolveSiteUrl(process.env.REACT_APP_SITE_URL);
  const fullUrl = url.startsWith('http') ? url : `${siteUrl}${url.startsWith('/') ? url : `/${url}`}`;
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image.startsWith('/') ? image : `/${image}`}`;
  const canonicalUrl = (() => {
    if (!canonical) return fullUrl;
    if (canonical.startsWith('http')) return canonical;
    const path = canonical.startsWith('/') ? canonical : `/${canonical}`;
    return `${siteUrl}${path}`;
  })();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Robots — always one tag so it replaces the pre-JS fallback */}
      <meta
        name="robots"
        content={`${noindex ? 'noindex' : 'index'}, ${nofollow ? 'nofollow' : 'follow'}`}
      />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImageUrl} />
      <meta property="og:site_name" content="Fixome" />
      <meta property="og:locale" content="en_IN" />
      
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      
      {/* Additional SEO Tags */}
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImageUrl} />
      
      {/* Additional Meta */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="language" content="English" />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

SEOHead.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  keywords: PropTypes.string,
  image: PropTypes.string,
  url: PropTypes.string,
  type: PropTypes.string,
  author: PropTypes.string,
  publishedTime: PropTypes.string,
  modifiedTime: PropTypes.string,
  structuredData: PropTypes.object,
  canonical: PropTypes.string,
  noindex: PropTypes.bool,
  nofollow: PropTypes.bool,
};

export default SEOHead;

